import {
  graphql,
  useStaticQuery,
} from 'gatsby';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import LinkChd from '../../LinkChd';

type FooterProps = {
  lang: string;
}

export default function Footer({ lang }: FooterProps) {
  const footerQuery = useStaticQuery(graphql`
   {
    contentfulFooter(title: {eq: "Main Footer"}) {
        node_locale
        id
        logo {
          ... on ContentfulExternalLink   {
            id
            internal {
              type
            }
            icon {
              url
            }
            url
          }
          ... on ContentfulSlug  {
            id
            internal {
              type
            }
            icon {
              url
            }
            slug
          }
        }
        missionNote {
          missionNote
        }
        buttonsNote
        footerButtons {
          ... on ContentfulExternalLink {
            id
            internal {
              type
            }
            url
            title
            node_locale
          }
          ... on ContentfulSlug  {
            id
            internal {
              type
            }
            slug
            title
            node_locale
          }
        }
        socialMediaList {
          navigationMenuItems {
            ... on ContentfulExternalLink {
              id
              internal {
                type
              }
              url
              title
              icon {
                url
                description
              }
            }
          }
        }
        footerInternalLinks {
          ... on ContentfulExternalLink {
          id
          internal {
            type
          }
          url
          title
          node_locale
          }
          ... on ContentfulSlug  {
          id
          internal {
            type
          }
          slug
          title
          node_locale
          }
        }
        taxNote
        footnote
    }
  }
`);

  const { contentfulFooter } = footerQuery;

  const {
    logo, footnote, missionNote, taxNote, buttonsNote,
  } = contentfulFooter;

  const socialMediaIcons = contentfulFooter.socialMediaList.navigationMenuItems
    .map((socialMediaItem:any) => (
      <a key={socialMediaItem.id} href={socialMediaItem.url} target="_blank" rel="noreferrer" className="rounded-full bg-white hover:bg-[#6A9C32] transition-all">
        <img
          loading="lazy"
          src={socialMediaItem.icon?.url}
          width="35px"
          alt={socialMediaItem.icon?.description}
          className="transition-all social-footer-icons"
        />
      </a>
    ));

  const footerButtons = contentfulFooter.footerButtons.map((button:any) => (
    <LinkChd key={button.id} link={button} linkStyles="text-white shadow-sm rounded-full bg-chdBlue hover:bg-white hover:text-chdBlue whitespace-nowrap block px-[16px] py-[12px] leading-tight font-bold tracking-[2.1px] uppercase text-[14px] transition-all w-full xl:w-auto" />
  ));

  const footerLinks = contentfulFooter.footerInternalLinks.map((link:any, index:number) => {
    let bulletStyles = '';
    if (index === 2 || index === 5) {
      bulletStyles = 'hidden lg:inline lg:text-[10.8pt] lg:text-chdBlue-light lg:mx-[10px]';
    } else if (index) {
      bulletStyles = ' text-[10.8pt] text-chdBlue-light mx-[10px]';
    } else {
      bulletStyles = 'hidden';
    }
    return (
      <li key={link.id}>
        <span className={bulletStyles}>&bull;</span>
        <LinkChd link={link} linkStyles="text-chdBlue-light hover:text-white hover:underline transition-all" />
      </li>
    );
  });

  return (
    <footer className="flex items-center justify-center w-full bg-darkGreyBlue border-t-1 border-solid border-[#eaeaea] mt-auto text-white">
      <div className="container px-4 pt-[20px] pb-[40px] text-center gap-x-[60px] max-w-[1200px] md:px-0 grid xl:grid-cols-2 xl:text-left xl:pt-[80px] xl:pb-[76px]">
        <div className="xl:order-1 xl:row-start-1 xl:row-end-4">
          <LinkChd wrap link={logo} linkStyles="flex items-center justify-center px-2 mb-[27px] xl:justify-start xl:mb-[20px]">
            <img loading="lazy" src={logo.icon?.url} alt={logo.icon?.description} className="w-full max-w-[203px] xl:max-w-[244px]" />
          </LinkChd>
          <div className="max-w-2xl mx-auto mt-4 text-[14px] leading-[18px] mb-[22px] tracking-[0.14px] px-[20px] xl:text-[16px] xl:leading-[22px] xl:mb-[14px] xl:px-0">
            <p>
              <strong className="font-bold">
                Children&apos;s Health Defense
                <sup>&#174;</sup>
              </strong>
              {missionNote.missionNote}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-start justify-center gap-x-[8px] gap-y-[11px] max-w-[275px] sm:max-w-full xl:max-w-full xl:justify-start mb-[34px] mx-auto xl:m-0 xl:order-5">
          {socialMediaIcons}
        </div>
        <div className="xl:order-3 xl:col-start-2 mb-[21px] xl:mb-0">
          <div className="leading-[22px] uppercase font-bold tracking-[1.6px] mb-[14px] xl:text-[24px] xl:mb-[20px]">
            {buttonsNote}
          </div>
          <div className="flex justify-between max-w-[265px] gap-[10px] mx-auto xl:max-w-full xl:flex-nowrap xl:justify-start xl:gap-x-[21px]">
            {footerButtons}
          </div>
        </div>
        <p className="leading-[16.5px] mb-[20px] italic text-[14px] max-w-[280px] mx-auto text-[#ccc] xl:order-4 xl:col-start-2 xl:col-end-3 xl:max-w-full xl:w-full xl:self-start xl:mb-0">
          {taxNote}
        </p>
        <div className="xl:order-5 xl:row-start-4 xl:col-start-1 xl:col-end-3 xl:mt-[75px]">
          <div className="max-w-[320px] lg:max-w-full mx-auto mb-[14px] xl:mb-[22px]">
            <ul className="flex flex-wrap text-[14px] items-center justify-center leading-[24.5px] tracking-[.07px] xl:text-[16px]">
              {footerLinks}
            </ul>
          </div>
          <p className="mt-4 text-[14px] max-w-[285px] mx-auto xl:max-w-full text-center">
            &copy; 2016 -
            {' '}
            {(new Date().getFullYear())}
            {' '}
            Children&apos;s Health Defense
            <sup>&#174; </sup>
            <span className="hidden"> &bull; </span>
            {footnote}
          </p>
        </div>
      </div>
    </footer>
  );
}
